import { Box, Checkbox, FormControlLabel, Grid, MenuItem, Select, TextField, Tooltip } from "@mui/material";
import * as React from "react";
import { ListExternalSystemLine } from "source/components/framework/List/ListExternalSystemLine";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { Base } from "ui/Scripts/source/framework/base";
import { FileAttachment, IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { removeConfirmationWord } from "ui/Scripts/source/models/consts";
import { ConfirmationDialogResult, CultureCode, EnumHelper, InstantMessageUserInvitationAcceptType, RecordEmailNotificationType, UserRemoveType, UserType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { SaveAttachmentBase64 } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/models/common/saveAttachmentBase64";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import { IUserExternalSystem, UserSaveExternalSystem } from "../../../models/externalSystem/userExternalSystem";
import { IUserIntraModule, UserSaveIntraModule } from "../../../models/intraModule/userIntraModule";
import * as userService from "../../../models/services/userService";
import { ISessionState } from "../../../models/session/sessionState";
import { IUserEditItem } from "../../../models/user/userEditItem";
import { UserEditSaveSuccess } from "../../../models/user/userEditSaveSuccess";
import { IUserGroupListItem } from "ui/Scripts/source/models/userGroup/userGroupListItem";
import { DatePicker } from "../../framework/DateTime/DatePicker";
import { Dialog } from "../../framework/Dialog/Dialog";
import { DialogItemsNav } from "../../framework/Dialog/DialogItemsNav";
import { DialogTabs } from "../../framework/Dialog/DialogTabs";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { ListHeaderLine } from "../../framework/List/ListLine";
import { UserEditChangePasswordDialog } from "./UserEditChangePasswordDialog";
import { UserEditDialogUserGroupList } from "./UserEditDialogUserGroupList";
import { UserEditRemoveSelectTypeDialog } from "./UserEditRemoveSelectTypeDialog";
import { ChangePasswordButton } from "source/components/framework/Button/ChangePasswordButton";
import { ICompanyMenuItem } from "ui/Scripts/source/models/company/companyMenuItem";
import { SendButton } from "source/components/framework/Button/SendButton";
import { UserEditSendEmailDialog } from "./UserEditSendEmailDialog";
import { IUserTokenData } from "source/models/user/userTokenData";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import { Dropdown } from "ui/Scripts/source/components/framework/Dropdown/Dropdown";
import { ICodeTitle } from "source/models/common/codeTitle";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { FormPassword } from "source/components/framework/FormPassword";
import { IUserMicrosoftAccount } from "ui/Scripts/source/models/user/userMicrosoftAccount";

/* eslint-disable no-unused-vars */
export const enum UserDialogMenuItem {
    General = "1",
    NotificationSettings = "2",
    UserGroups = "3",
    ExternalSystems = "4",
    IntraModules = "5"
}
/* eslint-enable no-unused-vars */

export interface IUserEditDialogProp {
    sessionState: ISessionState;
    companyId: string;
    userId: string;
    classes?: string;
    openedFromRecordUser?: boolean;
    recordUserPicture?: string;
    recordUserFirstName?: string;
    recordUserLastName?: string;
    recordUserEmail?: string;
    hasPrevious: boolean;
    hasNext: boolean;
    userType: UserType;
    defaultMenuItem: UserDialogMenuItem;
    onSaved: (userId: string, userCode: string) => void;
    onClose: (userDataWasModified: boolean) => void;
    onRefreshList: () => Promise<void>;
    onNewSaved: (userId: string, userCode: string, savedIsQrCodeAccount: boolean) => void;
    onPrevious: () => void;
    onNext: () => void;
    onRefreshSessionState: () => void;
}

export interface IUserEditDialogState {
    activeMenuItem: UserDialogMenuItem;
    isLoading: boolean;
    userDataWasModified: boolean;
    file: IFileAttachment;
    user: IUserEditItem;
    savedEmail: string;
    picture: string;
    resetPicture: boolean;
    id: string;
    code: string;
    firstName: string;
    lastName: string;
    email: string;
    comment: string;
    endDateStr: string;
    maxEndDate: number;
    maxEndDateStr: string;
    userType: UserType;
    portalUseType: number;
    emailNotificationType: number;
    pushNotificationType: number;
    recordEmailNotificationType: number;
    recordEmailNotificationShowExpired: boolean;
    instantMessageUserInvitationAcceptType: number;
    defaultCulture: string;
    qrCodeId: string;
    qrCodeAuthenticationTimeout: number;
    rowId: string;
    newPassword: string;
    newPasswordConfirmation: string;
    userGroups: IUserGroupListItem[];
    externalSystems: IUserExternalSystem[];
    intraModules: IUserIntraModule[];
    companies: ICompanyMenuItem[];
    companiesRequireUserGroups: ICodeTitle[];
    loginCompanyCode: string;
    allowedCompanies: ICompanyMenuItem[];
    qrCodeLoginUrlTemplate: string;
    companyCanUseInstantMessages: boolean;
    showChangePasswordDialog: boolean;
    minPasswordLength: number;
    tokenData: IUserTokenData;
    otherMadeTokenData: boolean;
    showRemoveTypeDialog: boolean;
    showSendEmailDialog: boolean;
    microsoftAccount: IUserMicrosoftAccount;
}

export class UserEditDialog extends React.Component<IUserEditDialogProp, IUserEditDialogState> {
    private orgStateHash: string = "";

    constructor(props: IUserEditDialogProp) {
        super(props);
        const maxEndDate = Base.getNowUtcDate().addYears(2).getTime();
        this.state = {
            activeMenuItem: Base.isNullOrUndefined(props.defaultMenuItem) ? UserDialogMenuItem.General : props.defaultMenuItem,
            isLoading: false,
            userDataWasModified: false,
            file: null,
            user: null,
            savedEmail: "",
            picture: "",
            resetPicture: false,
            id: "",
            code: "",
            firstName: "",
            lastName: "",
            email: "",
            comment: "",
            endDateStr: "",
            maxEndDate,
            maxEndDateStr: Base.utcTimeToDateStr(maxEndDate),
            userType: UserType.Normal,
            portalUseType: 0,
            emailNotificationType: 0,
            pushNotificationType: 0,
            recordEmailNotificationType: RecordEmailNotificationType.NotInUse,
            recordEmailNotificationShowExpired: false,
            instantMessageUserInvitationAcceptType: 0,
            defaultCulture: "",
            qrCodeId: "",
            qrCodeAuthenticationTimeout: 60,
            rowId: "",
            newPassword: "",
            newPasswordConfirmation: "",
            userGroups: [],
            externalSystems: [],
            intraModules: [],
            companies: [],
            companiesRequireUserGroups: [],
            loginCompanyCode: "",
            allowedCompanies: [],
            qrCodeLoginUrlTemplate: "",
            companyCanUseInstantMessages: false,
            showChangePasswordDialog: false,
            minPasswordLength: 8,
            showRemoveTypeDialog: false,
            showSendEmailDialog: false,
            tokenData: null,
            otherMadeTokenData: false,
            microsoftAccount: null
        };
    }

    getAllwedCompanies = (companies: ICompanyMenuItem[], userGroups: IUserGroupListItem[]) => {
        const userGroupCompanyCodes = Base.getUniqueStringItems(userGroups.map(i => i.companyCode));
        return companies.filter(i => userGroupCompanyCodes.indexOf(i.code) >= 0);
    };

    loadData = async (id: string) => {
        const props = this.props;
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => userService.getUserEdit(props.companyId, id, props.userType), false, false, () => this.setState({ isLoading: false }));
        if (!result) return;
        const user = result.user;
        const isNew = user.isNew();
        const isQrCodeAccount = EnumHelper.isEqual(user.userType, UserType.QrCode);
        const newPassword = isNew
            ? (isQrCodeAccount
                ? Base.getGuid()
                : Base.generatePassword(Math.max(result.minPasswordLength, 16)))
            : "";
        const state = {
            ...this.state,
            userDataWasModified: false,
            user,
            savedEmail: user.email,
            resetPicture: isNew && props.recordUserPicture ? false : !user.picture,
            picture: isNew && props.recordUserPicture ? props.recordUserPicture : user.picture,
            file: isNew && props.recordUserPicture
                ? await FileAttachment.photoToFileAttachment(props.recordUserPicture, 0)
                : (isNew && isQrCodeAccount
                    ? await FileAttachment.photoToFileAttachment(user.picture, 0)
                    : null
                ),
            id: user.id,
            code: user.code,
            firstName: isNew && props.recordUserFirstName ? props.recordUserFirstName : user.firstName,
            lastName: isNew && props.recordUserLastName ? props.recordUserLastName : user.lastName,
            email: isNew && props.recordUserEmail ? props.recordUserEmail : user.email,
            comment: user.comment,
            endDateStr: user.endDate ? Base.utcTimeToDateStr(user.endDate) : "",
            userType: user.userType,
            portalUseType: user.portalUseType,
            emailNotificationType: user.emailNotificationType,
            pushNotificationType: user.pushNotificationType,
            recordEmailNotificationType: user.recordEmailNotificationType,
            recordEmailNotificationShowExpired: user.recordEmailNotificationShowExpired,
            instantMessageUserInvitationAcceptType: user.instantMessageUserInvitationAcceptType,
            defaultCulture: user.defaultCulture,
            qrCodeId: user.qrCodeId,
            qrCodeAuthenticationTimeout: user.qrCodeAuthenticationTimeout || 60,
            rowId: user.rowId,
            userGroups: result.userGroups,
            externalSystems: result.externalSystems,
            intraModules: result.intraModules,
            companies: result.companies,
            companiesRequireUserGroups: result.companiesRequireUserGroups,
            loginCompanyCode: user.loginCompanyCode,
            allowedCompanies: this.getAllwedCompanies(result.companies, result.userGroups),
            qrCodeLoginUrlTemplate: result.qrCodeLoginUrlTemplate,
            companyCanUseInstantMessages: result.companyCanUseInstantMessages,
            minPasswordLength: result.minPasswordLength,
            tokenData: result.tokenData,
            otherMadeTokenData: !!result.tokenData && result.tokenData.modifyUserId !== id,
            newPassword,
            newPasswordConfirmation: newPassword,
            microsoftAccount: user.microsoftAccount,
        };
        this.setState(state);
        this.orgStateHash = (await this.getSaveDataFromState(props, state)).hash;
    };

    componentDidMount(): void {
        this.loadData(this.props.userId);
    }

    async componentDidUpdate(prevProps: IUserEditDialogProp, prevState: IUserEditDialogState): Promise<void> {
        const props = this.props;
        const state = this.state;
        const prevDefaultMenuItem = Base.isNullOrUndefined(prevProps.defaultMenuItem) ? UserDialogMenuItem.General : prevProps.defaultMenuItem;
        const defaultMenuItem = Base.isNullOrUndefined(props.defaultMenuItem) ? UserDialogMenuItem.General : props.defaultMenuItem;
        if (prevProps.companyId === props.companyId && prevProps.userId === props.userId) return;
        const cancelResult = await AppUtils.cancel((await this.getSaveDataFromState(props, state)).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await this.saveToDb()) return;
            if (!props.onRefreshList) {
                await props.onRefreshList();
            }
        }
        await this.loadData(props.userId);
        if (prevDefaultMenuItem !== defaultMenuItem) {
            this.setState({
                activeMenuItem: defaultMenuItem
            });
        }
    }

    handleToggleActiveMenuItem = (userDialogMenuItem: UserDialogMenuItem) => {
        this.setState({ activeMenuItem: userDialogMenuItem });
    };

    checkUserCode = async (userCode: string) => {
    };

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "xts") {
            const isQrCodeAccount = EnumHelper.isEqual(this.state.user.userType, UserType.QrCode);
            if (isQrCodeAccount) {
                this.setState({ code: value, lastName: value });
            } else {
                this.setState({ code: value });
            }
        } else if (name === "firstName") {
            this.setState({ firstName: value });
        } else if (name === "lastName") {
            this.setState({ lastName: value });
        } else if (name === "email") {
            this.setState({ email: value });
        } else if (name === "comment") {
            this.setState({ comment: value });
        } else if (name === "defaultCulture") {
            this.setState({ defaultCulture: value });
        } else if (name === "recordEmailNotificationType") {
            this.setState({ recordEmailNotificationType: value.toInteger() });
        } else if (name === "instantMessageUserInvitationAcceptType") {
            this.setState({ instantMessageUserInvitationAcceptType: value.toInteger() });
        } else if (name === "qrCodeAuthenticationTimeout") {
            this.setState({ qrCodeAuthenticationTimeout: value.toInteger() });
        }
    };

    handleBlur = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "xts") {
            const isQrCodeAccount = EnumHelper.isEqual(this.state.user.userType, UserType.QrCode);
            if (isQrCodeAccount) return;
            AppUtils.callService(() => userService.checkUserCode(this.state.id, this.props.companyId, value), false, true);
        }
    };

    handleEndDateChange = (value: string) => {
        this.setState({ endDateStr: value });
    };

    handleEndDateBlur = (value: string) => {
        this.setState({ endDateStr: value ? Base.utcTimeToDateStr(value.toUtcDate().getTime()) : "" });
    };

    recordEmailNotificationShowExpiredChange = (value: boolean) => {
        this.setState({ recordEmailNotificationShowExpired: value });
    };

    handleToggeExternalSystemInUse = (code: string, value: boolean) => {
        if (!code) return;
        const externalSystems = this.state.externalSystems.slice(0);
        const externalSystem = externalSystems.find(i => i.code === code);
        if (!externalSystem) return;
        externalSystem.inUse = value;
        this.setState({ externalSystems });
    };

    handleChangeExternalSystemExternalCode = (event) => {
        const target = event.target;
        const value: string = target.value;
        const code: string = target.name;
        if (!code) return;
        const externalSystems = this.state.externalSystems.slice(0);
        const externalSystem = externalSystems.find(i => i.code === code);
        if (!externalSystem) return;
        externalSystem.externalCode = value;
        this.setState({ externalSystems });
    };

    handleToggeIntraModuleInUse = (code: string, value: boolean) => {
        if (!code) return;
        const intraModules = this.state.intraModules.slice(0);
        const intraModule = intraModules.find(i => i.code === code);
        if (!intraModule) return;
        intraModule.inUse = value;
        this.setState({ intraModules });
    };

    handleChangeIntraModuleExternalCode = (event) => {
        const target = event.target;
        const value: string = target.value;
        const code: string = target.name;
        if (!code) return;
        const intraModules = this.state.intraModules.slice(0);
        const intraModule = intraModules.find(i => i.code === code);
        if (!intraModule) return;
        intraModule.externalCode = value;
        this.setState({ intraModules });
    };

    handleSetPicture = async (file: File) => {
        if (!file) return;
        const attachment = await FileAttachment.fileToFileAttachment(file, 0);
        if (!attachment) return;
        if (!AppUtils.validateFileAttachment(attachment)) return;
        this.setState({ file: attachment, resetPicture: false, picture: attachment.thumbnail });
    };

    handleRemovePicture = () => {
        this.setState({ file: null, resetPicture: true, picture: "" });
    };

    handleOnChangePassword = () => {
        this.setState({
            showChangePasswordDialog: true
        });
    };

    handleUserChangePasswordDialogOk = (rowId: string) => {
        this.setState({
            showChangePasswordDialog: false,
            rowId
        });
        this.orgStateHash = SaveDataObj.updateHashValue(this.orgStateHash, "rowId", rowId);
    };

    handleUserChangePasswordDialogCancel = () => {
        this.setState({
            showChangePasswordDialog: false
        });
    };

    // #region Email
    handleOnShowEmailDialog = () => {
        this.setState({
            showSendEmailDialog: true
        });
    };

    handleCloseSendEmailDialog = (tokenData: IUserTokenData) => {
        this.setState({
            showSendEmailDialog: false,
            tokenData: !tokenData
                ? this.state.tokenData
                : tokenData
        });
    };
    // #endregion Email

    // #region User Groups
    handleUserGroupsRemoved = (ids: string[]) => {
        if (!ids || ids.length < 1) return;
        const state = this.state;
        const userGroups = state.userGroups.filter(i => ids.indexOf(i.id) < 0);
        this.setState({
            userGroups,
            allowedCompanies: this.getAllwedCompanies(state.companies, userGroups),
            userDataWasModified: true
        });
    };
    
    handleUserGroupsAdded = (newUserGroups: IUserGroupListItem[]) => {
        if (!newUserGroups || newUserGroups.length < 1) return;
        const state = this.state;
        const userGroups = state.userGroups.concat(newUserGroups);
        this.setState({
            userGroups,
            allowedCompanies: this.getAllwedCompanies(state.companies, userGroups),
            userDataWasModified: true
        });
    };
    // #endregion User Groups

    getSaveDataFromState = async (props: IUserEditDialogProp, state: IUserEditDialogState): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("rowId", state.rowId);
        result.append("companyId", props.companyId);
        result.append("id", state.id);
        result.append("code", state.code);
        result.append("userType", state.userType);
        result.append("firstName", state.firstName);
        result.append("lastName", state.lastName);
        result.append("email", state.email);
        result.append("loginCompanyCode", state.loginCompanyCode);
        result.append("endDate", state.endDateStr ? state.endDateStr.toUtcDate().getTime() : null);
        result.append("newPassword", state.newPassword);
        result.append("newPasswordConfirmation", state.newPasswordConfirmation);
        result.append("defaultCulture", state.defaultCulture);
        result.append("qrCodeId", state.qrCodeId);
        result.append("qrCodeAuthenticationTimeout", state.qrCodeId ? state.qrCodeAuthenticationTimeout : null);
        result.append("comment", state.comment);
        result.append("portalUseType", state.portalUseType);
        result.append("emailNotificationType", state.emailNotificationType);
        result.append("pushNotificationType", state.pushNotificationType);
        result.append("recordEmailNotificationType", state.recordEmailNotificationType);
        result.append("recordEmailNotificationShowExpired", state.recordEmailNotificationShowExpired);
        result.append("instantMessageUserInvitationAcceptType", state.instantMessageUserInvitationAcceptType);
        result.append("resetImage", state.resetPicture);
        if (state.file) {
            result.append("attachments", await SaveAttachmentBase64.fromFileAttachments([state.file]));
        }
        // externalSystems
        result.append("externalSystems", state.externalSystems.reduce((result, externalSystem) => {
            if (externalSystem.inUse) {
                result.push(new UserSaveExternalSystem(externalSystem));
            }
            return result;
        }, []));
        // intraModules
        result.append("intraModules", state.intraModules.reduce((result, intraModule) => {
            if (intraModule.inUse) {
                result.push(new UserSaveIntraModule(intraModule));
            }
            return result;
        }, []));
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const props = this.props;
        const state = this.state;
        const isNew = state.user.isNew();
        const isQrCodeAccount = EnumHelper.isEqual(state.userType, UserType.QrCode);
        return new Promise<boolean>((resolve) => {
            if (!state.code) {
                AppUtils.showErrorMessage(Translations.UserCodeMustBeDefined);
                return resolve(false);
            }
            if (!isQrCodeAccount && isNew && !state.newPassword) {
                AppUtils.showErrorMessage(Translations.UserPasswordMustBeDefined);
                return resolve(false);
            }
            if (!isQrCodeAccount && !state.firstName) {
                AppUtils.showErrorMessage(Translations.FirstNameMustBeDefined);
                return resolve(false);
            }
            if (!state.lastName) {
                AppUtils.showErrorMessage(!isQrCodeAccount ? Translations.LastNameMustBeDefined : Translations.NameMustBeDefined);
                return resolve(false);
            }
            if (!isQrCodeAccount && !Base.isValidEmailAddress(state.email)) {
                AppUtils.showErrorMessage(Translations.EmailAddressMustBeValid);
                return resolve(false);
            }
            if (isNew && EnumHelper.isEqual(state.userType, UserType.Normal)) {
                if (!Base.isValidUserCode(state.code)) {
                    AppUtils.showErrorMessage(Translations.UserCodeMustBeValid);
                    return resolve(false);
                }
                if (!props.sessionState.isSuperAdmin) {
                    const errorMessage = Base.isValidPassword(state.newPassword, state.minPasswordLength);
                    if (errorMessage) {
                        AppUtils.showErrorMessage(errorMessage);
                        return resolve(false);
                    }
                }
                if (state.newPassword !== state.newPasswordConfirmation) {
                    AppUtils.showErrorMessage(Translations.PasswordConfirmationDoesNotMatchPassword);
                    return resolve(false);
                }
            }
            if (isQrCodeAccount) {
                if (!state.endDateStr) {
                    AppUtils.showErrorMessage(Translations.UserEndDateMustBeDefined);
                    return resolve(false);
                }
                const endDate = state.endDateStr.toUtcDate().getTime();
                if (endDate > state.maxEndDate) {
                    AppUtils.showErrorMessage(String.format(Translations.UserEndDateMustBeLessOrEqualToParameter, Base.utcTimeToDateStr(state.maxEndDate)));
                    return resolve(false);
                }
            }
            for (const externalSystem of state.externalSystems) {
                if (!externalSystem.inUse) continue;
                if (!externalSystem.externalCode) {
                    AppUtils.showErrorMessage(Translations.ExternalSystemUserCodeMustBeDefined);
                    return resolve(false);
                }
            }
            for (const intraModule of state.intraModules) {
                if (!intraModule.inUse) continue;
                if (!intraModule.externalCode) {
                    AppUtils.showErrorMessage(Translations.ExternalIntraSystemUserTokenMustBeDefined);
                    return resolve(false);
                }
            }
            //validateDefaultEventHandlers
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<UserEditSaveSuccess> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        if (state.user.isNew() && state.activeMenuItem === UserDialogMenuItem.General) {
            this.setState({
                activeMenuItem: state.userType !== UserType.QrCode
                    ? UserDialogMenuItem.NotificationSettings
                    : UserDialogMenuItem.UserGroups
            });
            return;
        }
        const saveData = await this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => userService.saveUserEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        const stateChange = { id: result.id, rowId: result.rowId, savedEmail: state.email };
        this.orgStateHash = (await this.getSaveDataFromState(props, { ...state, ...stateChange })).hash;
        this.setState(stateChange);
        //Refresh SessionState
        if (result.refreshSessionState) {
            props.onRefreshSessionState();
        }
        return result;
    };

    handleSave = async (closeDialog: boolean) => {
        const props = this.props;
        const state = this.state;
        const result = await this.saveToDb();
        if (!result) return;
        if (!state.user.isNew()) {
            if (!closeDialog) {
                this.loadData(result.id);
                props.onSaved(result.id, state.code);
                return;
            }
            props.onClose(true);
        } else {
            props.onNewSaved(result.id, state.code, EnumHelper.isEqual(state.userType, UserType.QrCode));
        }
    };

    remove = async (removeType: UserRemoveType) => {
        const props = this.props;
        const state = this.state;
        if (!state.user || state.user.isNew()) return;
        // Call server
        const result = await AppUtils.callService(() => userService.removeUser(state.user.id, props.companyId, removeType));
        if (!result) return;
        this.setState({
            showRemoveTypeDialog: false
        });
        props.onClose(true);
    };

    handleUserEditRemoveSelectTypeDialogOk = async (removeType: UserRemoveType) => {
        await this.remove(removeType);
    };

    handleUserEditRemoveSelectTypeDialogCancel = () => {
        this.setState({
            showRemoveTypeDialog: false
        });
    };

    handleRemove = async () => {
        const state = this.state;
        if (state.user.isNew()) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveUserParameter, state.user.code), false, removeConfirmationWord) === ConfirmationDialogResult.Yes) {
            if (state.user.recordUserId) {
                this.setState({
                    showRemoveTypeDialog: true
                });
            } else {
                await this.remove(UserRemoveType.RemoveUser);
            }
        }
    };

    handleQrCodePrint = async () => {
        const state = this.state;
        if (state.user.isNew()) return;
        const fileName = await AppUtils.callService(() => userService.getUserQrCodePrint(this.props.companyId, state.user.id));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.PrintParameterDownloaded, fileName));
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel((await this.getSaveDataFromState(props, state)).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose(state.userDataWasModified);
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut((await this.getSaveDataFromState(props, this.state)).hash, this.orgStateHash,
            async () => !!await this.saveToDb(),
            props.onRefreshList,
            (newStateHash) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    renderCompany = (company: ICompanyMenuItem) => {
        return <Box sx={{ display: "flex", alignItems: "center" }}>
            <AvatarImage
                size={20}
                name={company.name}
                image={company.logo}
            />
            <Box sx={{ paddingLeft: "10px" }}>{company.name}</Box>
        </Box>;
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.user) return null;
        const isNew = state.user.isNew();
        const userHasNotLoggedIn = !!state.user && !state.user.hasLoggedIn;
        const allowedCompany = state.allowedCompanies.find((company) => company.code === state.loginCompanyCode) ?? state.allowedCompanies[0];
        const isQrCodeAccount = EnumHelper.isEqual(state.userType, UserType.QrCode);
        const showMsAccount = !!state.microsoftAccount;
        return (
            <Dialog
                id="user-edit-dialog"
                maxWidth="md"
                maxHeight={isNew ? "836px" : "818px"}
                title={isNew
                    ? (!isQrCodeAccount ? Translations.AddUser : Translations.AddQrCodeUser)
                    : ((!isQrCodeAccount ? Translations.EditUser : Translations.EditQrCodeUser) + ": " + state.user.code)
                }
                dialogTabs={
                    <DialogTabs
                        activeTabId={state.activeMenuItem}
                        tabs={[
                            { id: UserDialogMenuItem.General, title: Translations.BasicInformation },
                            { id: UserDialogMenuItem.NotificationSettings, title: Translations.NotificationSettings, hidden: isQrCodeAccount },
                            { id: UserDialogMenuItem.UserGroups, title: Translations.UserGroups },
                            { id: UserDialogMenuItem.ExternalSystems, title: Translations.ExternalSystems, hidden: state.externalSystems.length < 1 || isQrCodeAccount },
                            { id: UserDialogMenuItem.IntraModules, title: Translations.ExternalIntraSystems, hidden: state.intraModules.length < 1 || isQrCodeAccount }
                        ]}
                        onChangeTab={this.handleToggleActiveMenuItem}
                    />
                }
                body={
                    <>
                        {state.activeMenuItem === UserDialogMenuItem.General &&
                            <>
                                <GridRow>
                                    <Grid item sm={showMsAccount ? 6 : 12}>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <Box display="flex" alignItems="center">
                                                    <AvatarImage
                                                        size={60}
                                                        marginRight="1rem"
                                                        id={state.code}
                                                        name={(state.lastName + " " + state.firstName).trim()}
                                                        image={state.picture}
                                                        editable={!isQrCodeAccount && !props.openedFromRecordUser}
                                                        tooltip={isQrCodeAccount ? "" : Translations.CkickToChangeThePicture}
                                                        removeTooltip={Translations.RemovePicture}
                                                        onChange={isQrCodeAccount ? null : this.handleSetPicture}
                                                        onClear={isQrCodeAccount || state.resetPicture ? null : this.handleRemovePicture}
                                                        onError={(message) => { AppUtils.showErrorMessage(message); }}
                                                    />
                                                    {(!isNew
                                                        ? <FormField title={Translations.UserName} required={isNew && !isQrCodeAccount} margin={0}>
                                                            {state.code}
                                                        </FormField>
                                                        : (!isQrCodeAccount
                                                            ? <FormField title={Translations.UserName} required={true} margin={0}>
                                                                <TextField type="text" name="xts" title={Translations.UserName} inputProps={{ maxLength: 50 }} value={state.code} onChange={this.handleChange} onBlur={this.handleBlur} />
                                                            </FormField>
                                                            : <GridRow>
                                                                <Grid item sm={6}>
                                                                    <FormField title={Translations.Name} margin={0} required>
                                                                        <TextField type="text" name="lastName" title={Translations.Name} inputProps={{ maxLength: 50 }} disabled={props.openedFromRecordUser} value={state.lastName} required={true} onChange={this.handleChange} />
                                                                    </FormField>
                                                                </Grid>
                                                                <Grid item sm={6}>
                                                                    <FormField title={Translations.AdditionalIdentifier} margin={0}>
                                                                        <TextField type="text" name="firstName" title={Translations.AdditionalIdentifier} inputProps={{ maxLength: 50 }} disabled={props.openedFromRecordUser} value={state.firstName} required={true} onChange={this.handleChange} />
                                                                    </FormField>
                                                                </Grid>
                                                            </GridRow>
                                                        )
                                                    )}
                                                </Box>
                                            </Grid>
                                        </GridRow>
                                    </Grid>
                                    {showMsAccount &&
                                        <Grid item sm={6}>
                                            <FormField title={Translations.MicrosoftAccount} margin={0}>
                                                <Box>
                                                    {state.microsoftAccount.username}
                                                </Box>
                                            </FormField>
                                        </Grid>
                                    }
                                </GridRow>
                                {isNew && !isQrCodeAccount &&
                                    <GridRow>
                                        <Grid item sm={6}>
                                            <FormField title={Translations.Password} required>
                                                <FormPassword
                                                    tooltip={Translations.Password}
                                                    maxLength={50}
                                                    value={state.newPassword}
                                                    required={true}
                                                    onChange={(newPassword) => this.setState({ newPassword })}
                                                />
                                            </FormField>
                                        </Grid>
                                        <Grid item sm={6}>
                                            <FormField title={Translations.ConfirmPassword} required>
                                                <FormPassword
                                                    tooltip={Translations.ConfirmPassword}
                                                    maxLength={50}
                                                    value={state.newPasswordConfirmation}
                                                    required={true}
                                                    onChange={(newPasswordConfirmation) => this.setState({ newPasswordConfirmation })}
                                                />
                                            </FormField>
                                        </Grid>
                                    </GridRow>
                                }
                                {!isQrCodeAccount &&
                                    <GridRow>
                                        <Grid item sm={6}>
                                            <FormField title={Translations.FirstName} required>
                                                <TextField type="text" name="firstName" title={Translations.FirstName} inputProps={{ maxLength: 50 }} disabled={props.openedFromRecordUser} value={state.firstName} required={true} onChange={this.handleChange} />
                                            </FormField>
                                        </Grid>
                                        <Grid item sm={6}>
                                            <FormField title={Translations.LastName} required>
                                                <TextField type="text" name="lastName" title={Translations.LastName} inputProps={{ maxLength: 50 }} disabled={props.openedFromRecordUser} value={state.lastName} required={true} onChange={this.handleChange} />
                                            </FormField>
                                        </Grid>
                                    </GridRow>
                                }
                                {!isNew && isQrCodeAccount &&
                                    <GridRow>
                                        <Grid item sm={6}>
                                            <FormField title={Translations.Name} required>
                                                <TextField type="text" name="lastName" title={Translations.Name} inputProps={{ maxLength: 50 }} disabled={props.openedFromRecordUser} value={state.lastName} required={true} onChange={this.handleChange} />
                                            </FormField>
                                        </Grid>
                                        <Grid item sm={6}>
                                            <FormField title={Translations.AdditionalIdentifier}>
                                                <TextField type="text" name="firstName" title={Translations.AdditionalIdentifier} inputProps={{ maxLength: 50 }} disabled={props.openedFromRecordUser} value={state.firstName} required={true} onChange={this.handleChange} />
                                            </FormField>
                                        </Grid>
                                    </GridRow>
                                }
                                <GridRow>
                                    {!isQrCodeAccount &&
                                        <Grid item sm={6}>
                                            <FormField title={Translations.EmailAddress}>
                                                <TextField type="text" name="email" title={Translations.EmailAddress} inputProps={{ maxLength: 50 }} disabled={props.openedFromRecordUser} value={state.email} required={true} onChange={this.handleChange} />
                                            </FormField>
                                        </Grid>
                                    }
                                    <Grid item sm={6}>
                                        <FormField title={Translations.UserEndDate + (isQrCodeAccount ? ` (${Translations.Maximum.toLocaleLowerCase()} ${state.maxEndDateStr})` : "")} required={isQrCodeAccount}>
                                            <DatePicker
                                                value={state.endDateStr}
                                                maxDate={isQrCodeAccount ? state.maxEndDateStr : null}
                                                onChange={this.handleEndDateChange}
                                            />
                                        </FormField>
                                    </Grid>
                                    {isQrCodeAccount &&
                                        <>
                                            <Grid item sm={3}>
                                                <FormField title={Translations.DefaultLanguage}>
                                                    <Select name="defaultCulture" title={Translations.DefaultLanguage} value={state.defaultCulture} onChange={this.handleChange}>
                                                        <MenuItem key={CultureCode.Fi} value={CultureCode.Fi}>{Translations.LanguageFinnish}</MenuItem>
                                                        <MenuItem key={CultureCode.En} value={CultureCode.En}>{Translations.LanguageEnglish}</MenuItem>
                                                    </Select>
                                                </FormField>
                                            </Grid>
                                            <Grid item sm={3}>
                                                <FormField title={Translations.QrCodeAuthenticationTimeout}>
                                                    <Select name="qrCodeAuthenticationTimeout" title={Translations.QrCodeAuthenticationTimeout} value={state.qrCodeAuthenticationTimeout.toString(10)} onChange={this.handleChange}>
                                                        <MenuItem key={60} value="60">{Translations.QrCodeAuthenticationTimeoutHour}</MenuItem>
                                                        <MenuItem key={24 * 60} value="1440">{Translations.QrCodeAuthenticationTimeoutDay}</MenuItem>
                                                        <MenuItem key={7 * 24 * 60} value="10080">{Translations.QrCodeAuthenticationTimeoutWeek}</MenuItem>
                                                    </Select>
                                                </FormField>
                                            </Grid>
                                        </>
                                    }
                                </GridRow>
                                <GridRow>
                                    <Grid item sm={6}>
                                        {!isQrCodeAccount &&
                                            <FormField title={Translations.DefaultLanguage}>
                                                <Select name="defaultCulture" title={Translations.DefaultLanguage} value={state.defaultCulture} onChange={this.handleChange}>
                                                    <MenuItem key={CultureCode.Fi} value={CultureCode.Fi}>{Translations.LanguageFinnish}</MenuItem>
                                                    <MenuItem key={CultureCode.En} value={CultureCode.En}>{Translations.LanguageEnglish}</MenuItem>
                                                </Select>
                                            </FormField>
                                        }
                                        {state.allowedCompanies.length > 1 &&
                                            <FormField title={Translations.LoginCompany}>
                                                <Dropdown
                                                    selectedTitle={this.renderCompany(allowedCompany)}
                                                    actions={state.allowedCompanies.map((company) => {
                                                        return {
                                                            title: this.renderCompany(company),
                                                            onClick: () => this.setState({ loginCompanyCode: company.code })
                                                        };
                                                    })}
                                                />
                                            </FormField>
                                        }
                                    </Grid>
                                    {!isNew && !isQrCodeAccount &&
                                        <Grid item sm={6}>
                                            <FormField title={Translations.Password}>
                                                <GridRow>
                                                    <Grid item sm={12}>
                                                        <ChangePasswordButton
                                                            fullWidth={true}
                                                            onClick={this.handleOnChangePassword}
                                                        >
                                                            {Translations.ChangePassword}
                                                        </ChangePasswordButton>
                                                    </Grid>
                                                    <Grid item sm={12}>
                                                        <SendButton
                                                            fullWidth={true}
                                                            disabled={!state.savedEmail}
                                                            onClick={this.handleOnShowEmailDialog}
                                                        >
                                                            {Translations.InvitationPasswordReset}
                                                        </SendButton>
                                                        {(state.otherMadeTokenData || userHasNotLoggedIn) &&
                                                            <Box sx={{ marginTop: ".5rem", display: "flex", alignItems: "center" }} title={state.otherMadeTokenData ? `${Translations.SendTime.toLocaleLowerCase()} (${Translations.ExpireTime.toLocaleLowerCase()})` : ""}>
                                                                {userHasNotLoggedIn &&
                                                                    <Tooltip title={Translations.UserHasNotLoggedIn}>
                                                                        <PersonOffIcon sx={{ marginRight: ".25rem", color: "error.light" }} />
                                                                    </Tooltip>
                                                                }
                                                                {state.otherMadeTokenData &&
                                                                    <Box>
                                                                        <Box component={"span"}>{Base.timeToDateTimeStr(state.tokenData.time, true)}</Box>
                                                                        <Box component={"span"}> (</Box>
                                                                        <Box component={"span"} sx={{ color: state.tokenData.expireTime < Base.getNowTicks() ? "error.main" : undefined }}>{Base.timeToDateTimeStr(state.tokenData.expireTime, true)}</Box>
                                                                        <Box component={"span"}>)</Box>
                                                                    </Box>
                                                                }
                                                            </Box>
                                                        }
                                                    </Grid>
                                                </GridRow>
                                            </FormField>
                                        </Grid>
                                    }
                                </GridRow>
                                <GridRow>
                                    <Grid item sm={!isQrCodeAccount ? 12 : 9}>
                                        <FormField title={Translations.Description}>
                                            <TextField name="comment" title={Translations.Description} inputProps={{ maxLength: 500 }} multiline minRows={!isQrCodeAccount ? 5 : 8} disabled={false} value={state.comment} required={true} onChange={this.handleChange} />
                                        </FormField>
                                    </Grid>
                                    {isQrCodeAccount &&
                                        <Grid item sm={3}>
                                            <FormField title={Translations.QrCode}>
                                                <Box
                                                    component="img"
                                                    src={state.picture}
                                                    sx={{
                                                        width: "100%",
                                                        height: "auto",
                                                        objectFit: "contain",
                                                    }}
                                                />
                                            </FormField>
                                        </Grid>
                                    }
                                </GridRow>
                                {isQrCodeAccount &&
                                    <GridRow>
                                        <Grid item sm={12}>
                                            <FormField title={Translations.Link}>
                                                {String.format(state.qrCodeLoginUrlTemplate, state.qrCodeId)}
                                            </FormField>
                                        </Grid>
                                    </GridRow>
                                }
                            </>
                        }
                        {state.activeMenuItem === UserDialogMenuItem.NotificationSettings &&
                            <>
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.NotificationSettings} margin={0}>
                                            <FormControlLabel
                                                label={Translations.EmailNotificationInUse}
                                                control={
                                                    <Checkbox
                                                        checked={state.emailNotificationType > 0}
                                                        onChange={(_e, checked: boolean) => {
                                                            this.setState({ emailNotificationType: checked ? 1 : 0 });
                                                        }}
                                                    />
                                                }
                                            />
                                            <FormControlLabel
                                                label={Translations.PushNotificationInUse}
                                                control={
                                                    <Checkbox
                                                        checked={state.pushNotificationType > 0}
                                                        onChange={(_e, checked: boolean) => {
                                                            this.setState({ pushNotificationType: checked ? 1 : 0 });
                                                        }}
                                                    />
                                                }
                                            />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                                {state.companyCanUseInstantMessages &&
                                    <GridRow>
                                        <Grid item sm={12}>
                                            <FormField title={Translations.InstantMessageSettings}>
                                                <Select name="instantMessageUserInvitationAcceptType" title={Translations.InstantMessageSettings} value={state.instantMessageUserInvitationAcceptType.toString(10)} onChange={this.handleChange}>
                                                    <MenuItem key={InstantMessageUserInvitationAcceptType.CannotInvite} value={InstantMessageUserInvitationAcceptType.CannotInvite.toString(10)}>{Translations.InstantMessageUserInvitationAcceptTypeCannotInvite}</MenuItem>
                                                    <MenuItem key={InstantMessageUserInvitationAcceptType.CanInvite} value={InstantMessageUserInvitationAcceptType.CanInvite.toString(10)}>{Translations.InstantMessageUserInvitationAcceptTypeCanInvite}</MenuItem>
                                                    <MenuItem key={InstantMessageUserInvitationAcceptType.AutoAcceptInvitation} value={InstantMessageUserInvitationAcceptType.AutoAcceptInvitation.toString(10)}>{Translations.InstantMessageUserInvitationAcceptTypeAutoAcceptInvitation}</MenuItem>
                                                </Select>
                                            </FormField>
                                        </Grid>
                                    </GridRow>
                                }
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.FurtherEducationReports}>
                                            <FormControlLabel
                                                label={<Box sx={{ paddingBottom: "0.5rem" }}>
                                                    <Box>{Translations.RecordEmailNotificationType}</Box>
                                                    <Box sx={{ fontSize: "12px" }}>{Translations.RecordEmailNotificationTypeLine2}</Box>
                                                </Box>}
                                                control={
                                                    <Checkbox
                                                        checked={state.recordEmailNotificationType > 0}
                                                        onChange={(_e, checked: boolean) => {
                                                            this.setState({
                                                                recordEmailNotificationType: checked ? RecordEmailNotificationType.Expiration090 : RecordEmailNotificationType.NotInUse,
                                                                recordEmailNotificationShowExpired: checked ? state.recordEmailNotificationShowExpired : false
                                                            });
                                                        }}
                                                    />
                                                }
                                            />
                                            {state.recordEmailNotificationType > 0 &&
                                                <>
                                                    <Select name="recordEmailNotificationType" title={Translations.RecordEmailNotificationType} value={state.recordEmailNotificationType.toString(10)} onChange={this.handleChange}>
                                                        <MenuItem key={RecordEmailNotificationType.Expiration090} value={RecordEmailNotificationType.Expiration090.toString(10)}>{Translations.ExpiringIn90Days}</MenuItem>
                                                        <MenuItem key={RecordEmailNotificationType.Expiration120} value={RecordEmailNotificationType.Expiration120.toString(10)}>{Translations.ExpiringIn120Days}</MenuItem>
                                                        <MenuItem key={RecordEmailNotificationType.Expiration180} value={RecordEmailNotificationType.Expiration180.toString(10)}>{Translations.ExpiringIn180Days}</MenuItem>
                                                    </Select>
                                                    <FormControlLabel
                                                        label={Translations.RecordEmailNotificationShowExpired}
                                                        control={
                                                            <Checkbox
                                                                checked={state.recordEmailNotificationShowExpired}
                                                                onChange={(_e, checked: boolean) => {
                                                                    this.setState({ recordEmailNotificationShowExpired: checked });
                                                                }}
                                                            />
                                                        }
                                                        sx={{ marginTop: ".5rem" }}
                                                    />
                                                </>
                                            }
                                        </FormField>
                                    </Grid>
                                </GridRow>
                            </>
                        }
                        {state.activeMenuItem === UserDialogMenuItem.UserGroups &&
                            <UserEditDialogUserGroupList
                                isReadOnly={isNew}
                                sessionState={props.sessionState}
                                userId={state.id}
                                companyId={props.companyId}
                                isQrCodeAccount={isQrCodeAccount}
                                userGroups={state.userGroups}
                                companies={state.companies}
                                companiesRequireUserGroups={state.companiesRequireUserGroups}
                                onRemoved={this.handleUserGroupsRemoved}
                                onAdded={this.handleUserGroupsAdded}
                            />
                        }
                        {state.activeMenuItem === UserDialogMenuItem.ExternalSystems &&
                            <>
                                <ListHeaderLine
                                    columns={[
                                        { size: 7, column: "name", content: Translations.Name },
                                        { size: 5, column: "code", content: Translations.ExternalSystemCode }
                                    ]}
                                />
                                {state.externalSystems.map((externalSystem) =>
                                    <ListExternalSystemLine
                                        key={externalSystem.code}
                                        code={externalSystem.code}
                                        name={externalSystem.name}
                                        checked={externalSystem.inUse}
                                        value={externalSystem.externalCode}
                                        onCheckChange={this.handleToggeExternalSystemInUse}
                                        onInputChange={this.handleChangeExternalSystemExternalCode}
                                    />
                                )}
                            </>
                        }
                        {state.activeMenuItem === UserDialogMenuItem.IntraModules &&
                            <>
                                <ListHeaderLine
                                    columns={[
                                        { size: 7, column: "name", content: Translations.Name },
                                        { size: 5, column: "userToken", content: Translations.UserToken }
                                    ]}
                                />
                                {state.intraModules.map((intraModule) =>
                                    <ListExternalSystemLine
                                        key={intraModule.code}
                                        code={intraModule.code}
                                        name={intraModule.name}
                                        checked={intraModule.inUse}
                                        value={intraModule.externalCode}
                                        rootUrl={appConfig.rootUrl}
                                        onCheckChange={this.handleToggeIntraModuleInUse}
                                        onInputChange={this.handleChangeIntraModuleExternalCode}
                                    />
                                )}
                            </>
                        }
                        {state.showChangePasswordDialog &&
                            <UserEditChangePasswordDialog
                                userId={state.id}
                                companyId={props.companyId}
                                minPasswordLength={state.minPasswordLength}
                                rowId={state.rowId}
                                onOk={this.handleUserChangePasswordDialogOk}
                                onCancel={this.handleUserChangePasswordDialogCancel}
                            />
                        }
                        {state.showRemoveTypeDialog &&
                            <UserEditRemoveSelectTypeDialog
                                onOk={this.handleUserEditRemoveSelectTypeDialogOk}
                                onCancel={this.handleUserEditRemoveSelectTypeDialogCancel}
                            />
                        }
                        {state.showSendEmailDialog &&
                            <UserEditSendEmailDialog
                                userId={state.id}
                                companyId={props.companyId}
                                onCancel={this.handleCloseSendEmailDialog}
                            />
                        }
                    </>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={this.handlePrevious}
                            onNext={this.handleNext}
                            createUser={!isNew ? state.user.createUser : null}
                            modifyUser={!isNew ? state.user.modifyUser : null}
                        />
                        <DialogButtons
                            isNew={isNew}
                            saveTitle={(isNew && (state.activeMenuItem === UserDialogMenuItem.General || state.activeMenuItem === UserDialogMenuItem.NotificationSettings)) ? Translations.Next : Translations.Save}
                            onCancel={this.handleCancel}
                            onRemove={state.user && !isNew && !props.openedFromRecordUser ? this.handleRemove : null}
                            onSave={() => this.handleSave(false)}
                            onPrint={(!isNew && isQrCodeAccount) ? this.handleQrCodePrint : null}
                        />
                    </Box>
                }
                onClose={this.handleCancel}/>
        );
    }
}